import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { name: "The Lip Booster", href: "#booster", testid: "nav-link-philosophy" },
  { name: "Results", href: "#before-after", testid: "nav-link-results" },
  { name: "For Professionals", href: "#professionals", testid: "nav-link-professionals" },
  { name: "Contact", href: "#contact", testid: "nav-link-contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <header
        data-testid="nav-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#FAF7F5]/85 backdrop-blur-xl border-b border-[#EADECE]/60"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className={`max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between ${
          scrolled ? "" : "drop-shadow-[0_1px_10px_rgba(28,20,23,0.55)]"
        }`}>
          <button
            data-testid="nav-logo-link"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`font-serif text-lg sm:text-xl tracking-[0.08em] whitespace-nowrap transition-colors duration-500 ${
              scrolled ? "text-[#1C1417]" : "text-[#FAF7F5]"
            }`}
          >
            SKINFILL <span className="italic text-[#D84A75]">BACIO</span>
            <sup className="text-[9px] ml-0.5">®</sup>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={l.testid}
                onClick={() => go(l.href)}
                className={`font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-[#D84A75] ${
                  scrolled ? "text-[#1C1417]" : "text-[#FAF7F5]/85"
                }`}
              >
                {l.name}
              </button>
            ))}
            <button
              data-testid="nav-order-stock-button"
              onClick={() => go("#contact")}
              className="ml-2 rounded-full bg-[#A31D4F] hover:bg-[#86143E] text-white font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-2.5 transition-colors duration-300"
            >
              Enquire Now
            </button>
          </nav>

          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className={`lg:hidden transition-colors duration-500 ${
              scrolled ? "text-[#1C1417]" : "text-[#FAF7F5]"
            }`}
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#1C1417] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 h-16">
              <span className="font-serif text-lg text-[#FAF7F5] tracking-[0.08em]">
                SKINFILL <span className="italic text-[#D84A75]">BACIO</span>
              </span>
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="text-[#FAF7F5]"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-7">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-3xl text-[#FAF7F5] hover:text-[#D84A75] transition-colors"
                >
                  {l.name}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                data-testid="nav-mobile-enquire-button"
                onClick={() => go("#contact")}
                className="mt-6 w-fit rounded-full bg-[#A31D4F] text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4"
              >
                Enquire Now
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
